import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../useAuth';
import api from '../utils/api';
import SEOHelmet from '../components/SEOHelmet';
import { savePlanIntent } from '../components/PlanIntentBanner';

const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface-raised)',
  border: '1px solid var(--color-border)',
  borderTop: '4px solid var(--color-primary)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-md)',
  padding: '2.5rem 2rem',
  width: '100%',
  maxWidth: '420px',
};

const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontWeight: 'var(--fw-semibold)',
  fontSize: 'var(--fs-sm)',
  color: 'var(--color-text)',
  display: 'block',
  marginBottom: '0.375rem',
};

const inputStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 'var(--fs-sm)',
  color: 'var(--color-text)',
  background: 'var(--color-surface-raised)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  width: '100%',
  padding: '0.55rem 0.85rem',
  outline: 'none',
  transition: 'border-color var(--transition-fast), box-shadow var(--transition-fast)',
};

const primaryBtnStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontWeight: 'var(--fw-semibold)',
  fontSize: 'var(--fs-base)',
  background: 'var(--color-primary)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  width: '100%',
  padding: '0.7rem 1rem',
  cursor: 'pointer',
  marginTop: '0.5rem',
  transition: 'background var(--transition-fast)',
};

// ── Plan intent data ──────────────────────────────────────────────────────────
interface PlanMeta { label: string; price: number; seats?: string; icon: string }

function getPlanMeta(plan: string, tier: string | null, interval: string): PlanMeta | null {
  if (!plan) return null;
  const ann = interval === 'annual';
  if (plan === 'solo') return { label: 'Solo Plan', price: ann ? 4 : 5, icon: 'bi-person-fill' };
  if (plan === 'pro') return { label: 'Pro Plan', price: ann ? 7 : 9, icon: 'bi-person-check-fill' };
  const tiers: Record<string, { monthly: number; annual: number; seats: string }> = {
    Starter:  { monthly: 59,  annual: 49,  seats: 'Up to 10 members' },
    Firm:     { monthly: 129, annual: 109, seats: 'Up to 30 members' },
    Practice: { monthly: 249, annual: 199, seats: 'Up to 100 members' },
  };
  const t = tier || 'Starter';
  const info = tiers[t] || tiers.Starter;
  return {
    label: `Team — ${t} Plan`,
    price: ann ? info.annual : info.monthly,
    seats: info.seats,
    icon: t === 'Practice' ? 'bi-buildings-fill' : 'bi-buildings',
  };
}

// ── PlanIntentBanner ─────────────────────────────────────────────────────────
const PlanIntentBanner: React.FC<{ meta: PlanMeta; interval: string }> = ({ meta, interval }) => (
  <div style={{
    display: 'flex', alignItems: 'flex-start', gap: '0.875rem',
    background: 'var(--color-accent-light)',
    border: '1px solid var(--color-accent)',
    borderRadius: 'var(--radius-md)',
    padding: '0.875rem 1rem',
    marginBottom: '1.5rem',
  }}>
    <i className={`bi ${meta.icon}`} style={{ fontSize: '1.25rem', color: 'var(--color-accent)', flexShrink: 0, marginTop: '0.1rem' }} />
    <div>
      <div style={{ fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-sm)', color: 'var(--color-primary)' }}>
        {meta.label}
      </div>
      <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', color: 'var(--color-text-muted)', marginTop: '0.2rem' }}>
        ${meta.price}/mo{interval === 'annual' ? ' · billed annually' : ''}
        {meta.seats ? ` · ${meta.seats}` : ''}
      </div>
      <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', color: 'var(--color-text-muted)', marginTop: '0.2rem' }}>
        Checkout opens automatically after you sign in.
      </div>
    </div>
  </div>
);

const Login = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const intentPlan     = searchParams.get('plan') || '';
  const intentTier     = searchParams.get('tier') || '';
  const intentInterval = searchParams.get('interval') || 'monthly';
  const planMeta = getPlanMeta(intentPlan, intentTier, intentInterval);

  // Only allow relative paths to prevent open-redirect attacks
  const rawRedirect = searchParams.get('redirect') || '';
  const redirectTo = rawRedirect.startsWith('/') && !rawRedirect.startsWith('//') ? rawRedirect : '';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  // Navigate to dashboard once user is authenticated.
  // If plan intent params are present, persist them to localStorage so the
  // site-wide PlanIntentBanner can show across all pages until dismissed.
  useEffect(() => {
    if (!user) return;
    if (intentPlan) {
      savePlanIntent({ plan: intentPlan, tier: intentTier, interval: intentInterval || 'monthly' });
    }
    navigate(redirectTo || '/dashboard');
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await api.post('/auth/login', { email, password });
      login(res.data.token);
      // Navigation handled by useEffect([user]) above.
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed. Please check your credentials.');
      setSubmitting(false);
    }
  };

  const focusStyle = (field: string): React.CSSProperties => ({
    ...inputStyle,
    borderColor: focusedField === field ? 'var(--color-border-focus)' : 'var(--color-border)',
    boxShadow: focusedField === field ? '0 0 0 3px rgba(42, 191, 191, 0.18)' : 'none',
  });

  // "Register" link preserves plan intent params
  const registerHref = intentPlan
    ? `/register?plan=${intentPlan}${intentTier ? `&tier=${intentTier}` : ''}&interval=${intentInterval}`
    : '/register';

  return (
    <>
    <SEOHelmet
      noindex={true}
      title="Sign In"
      description="Sign in to CEC Track to manage your continuing education credits and professional license renewal cycles."
      canonical="https://cectrack.com/login"
    />
    <div style={{
      minHeight: '80vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem 1rem',
      background: 'var(--color-surface)',
    }}>
      <div style={cardStyle}>
        {/* Header */}
        <div style={{ marginBottom: planMeta ? '1.25rem' : '2rem', textAlign: 'center' }}>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 'var(--fw-black)',
            fontSize: 'var(--fs-3xl)',
            color: 'var(--color-text)',
            margin: '0 0 0.375rem',
            lineHeight: 1.1,
          }}>
            Sign in
          </h1>
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-sm)',
            color: 'var(--color-text-muted)',
            margin: 0,
          }}>
            {planMeta ? 'Sign in to continue to checkout' : 'Welcome back to CEC Track'}
          </p>
        </div>

        {/* Plan intent banner */}
        {planMeta && (
          <PlanIntentBanner meta={planMeta} interval={intentInterval} />
        )}

        {/* Error */}
        {error && (
          <div style={{
            marginBottom: '1.25rem',
            padding: '0.7rem 1rem',
            background: 'var(--color-danger-light)',
            border: '1px solid var(--color-danger)',
            borderRadius: 'var(--radius-sm)',
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-sm)',
            color: 'var(--color-danger)',
          }}>
            <i className="bi bi-exclamation-circle me-2" />{error}
          </div>
        )}

        <form onSubmit={handleLogin}>
          {/* Email */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label htmlFor="email" style={labelStyle}>Email address</label>
            <input
              id="email"
              type="email"
              style={focusStyle('email')}
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onFocus={() => setFocusedField('email')}
              onBlur={() => setFocusedField(null)}
              required
              autoComplete="email"
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.375rem' }}>
              <label htmlFor="password" style={{ ...labelStyle, margin: 0 }}>Password</label>
              <Link to="/forgot-password" style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--fs-xs)',
                color: 'var(--color-accent)',
                textDecoration: 'none',
              }}>
                Forgot password?
              </Link>
            </div>
            <div style={{ position: 'relative' }}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                style={{ ...focusStyle('password'), paddingRight: '2.8rem' }}
                placeholder="Enter password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField(null)}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword(v => !v)}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--color-text-muted)',
                  padding: '0.2rem',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} style={{ fontSize: '1rem' }} />
              </button>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            style={{
              ...primaryBtnStyle,
              opacity: submitting ? 0.7 : 1,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={e => !submitting && ((e.currentTarget as HTMLButtonElement).style.background = 'var(--color-primary-hover)')}
            onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.background = 'var(--color-primary)')}
          >
            {submitting
              ? <><span className="spinner-border spinner-border-sm me-2" role="status" />{planMeta ? 'Signing in…' : 'Signing in…'}</>
              : planMeta ? 'Sign in & go to checkout' : 'Sign in'}
          </button>
        </form>

        {/* Footer links */}
        <div style={{
          marginTop: '1.5rem',
          textAlign: 'center',
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--fs-sm)',
          color: 'var(--color-text-muted)',
        }}>
          Don't have an account?{' '}
          <Link to={registerHref} style={{ color: 'var(--color-accent)', fontWeight: 'var(--fw-semibold)', textDecoration: 'none' }}>
            Register
          </Link>
        </div>
      </div>
    </div>
    </>
  );
};

export default Login;
